import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Paper,
  Checkbox,
  FormControlLabel,
  Radio,
  RadioGroup,
  FormControl,
  CircularProgress,
  Alert,
  IconButton,
  Divider,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import SaveIcon from '@mui/icons-material/Save';
import CloseIcon from '@mui/icons-material/Close';
import ImageIcon from '@mui/icons-material/Image';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';

import config from '../twh/config';
import { getFirstErrorMessage } from '../twh/functions';

const POPUP_GET = `${config.reactApi_baseUrl}/react-api/popup/popup.php`;
const POPUP_SAVE = `${config.reactApi_baseUrl}/react-api/popup/add-edit-popup.php`;
const POPUP_DELETE_IMAGE = `${config.reactApi_baseUrl}/react-api/popup/delete-popup-image.php`;

const POPUP_PDF_GET = `${config.reactApi_baseUrl}/react-api/popup/popup-pdf.php`;
const POPUP_PDF_SAVE = `${config.reactApi_baseUrl}/react-api/popup/add-edit-popup-pdf.php`;
const POPUP_DELETE_PDF = `${config.reactApi_baseUrl}/react-api/popup/delete-popup-pdf.php`;

export default function PopupBody() {
  // Image Popup State
  const [loadingImage, setLoadingImage] = useState(false);
  const [savingImage, setSavingImage] = useState(false);
  const [errorImage, setErrorImage] = useState(null);
  const [successImage, setSuccessImage] = useState(null);

  const [formImage, setFormImage] = useState({
    popup_id: '',
    title: '',
    description: '',
    button: '',
    button_url: '',
    button_target: '_blank',
    image: null,
    imagePreview: '',
    image_showOnMobile: 'Yes',
    popup_trigger: 'Only on the homepage',
    popup_trigger_url: '',
    popup_trigger_date: '',
    startDate: '',
    endDate: '',
    status: 'Active',
  });

  // PDF Popup State
  const [loadingPdf, setLoadingPdf] = useState(false);
  const [savingPdf, setSavingPdf] = useState(false);
  const [errorPdf, setErrorPdf] = useState(null);
  const [successPdf, setSuccessPdf] = useState(null);

  const [formPdf, setFormPdf] = useState({
    popup_id: '',
    pdf: null,
    pdfPreview: '',
    pdfName: '',
    popup_trigger: 'Only on the homepage',
    popup_trigger_url: '',
    popup_trigger_date: '',
    startDate: '',
    endDate: '',
    status: 'Active',
  });

  // Fetch Image Popup
  useEffect(() => {
    fetchExistingImagePopup();
    fetchExistingPdfPopup();
  }, []);

  const fetchExistingImagePopup = async () => {
    setLoadingImage(true);
    setErrorImage(null);
    try {
      const res = await fetch(POPUP_GET, { credentials: 'include' });
      const json = await res.json();
      if (json.status === true && json.data?.length > 0) {
        const popup = json.data[0];
        setFormImage((prev) => ({
          ...prev,
          popup_id: popup.id || '',
          title: popup.title || '',
          description: popup.description || '',
          button: popup.button || '',
          button_url: popup.button_url || '',
          button_target: popup.button_target || '_blank',
          imagePreview: popup.image ? `${config.reactApi_baseUrl}/media/popup/${popup.image}` : '',
          image_showOnMobile: popup.image_showOnMobile || 'Yes',
          popup_trigger: popup.popup_trigger || 'Only on the homepage',
          popup_trigger_url: popup.popup_trigger_url || '',
          popup_trigger_date: popup.popup_trigger_date || '',
          startDate: popup.startDate || '',
          endDate: popup.endDate || '',
          status: popup.status || 'Active',
        }));
      }
    } catch (err) {
      setErrorImage('Failed to load image popup data.');
    } finally {
      setLoadingImage(false);
    }
  };

  // Fetch PDF Popup
  const fetchExistingPdfPopup = async () => {
    setLoadingPdf(true);
    setErrorPdf(null);
    try {
      const res = await fetch(POPUP_PDF_GET, { credentials: 'include' });
      const json = await res.json();
      if (json.status === true && json.data?.length > 0) {
        const popup = json.data[0];
        setFormPdf((prev) => ({
          ...prev,
          popup_id: popup.id || '',
          pdfPreview: popup.pdf ? `${config.reactApi_baseUrl}/media/popup-pdf/${popup.pdf}` : '',
          pdfName: popup.pdf || '',
          popup_trigger: popup.popup_trigger || 'Only on the homepage',
          popup_trigger_url: popup.popup_trigger_url || '',
          popup_trigger_date: popup.popup_trigger_date || '',
          startDate: popup.startDate || '',
          endDate: popup.endDate || '',
          status: popup.status || 'Active',
        }));
      }
    } catch (err) {
      setErrorPdf('Failed to load PDF popup data.');
    } finally {
      setLoadingPdf(false);
    }
  };

  // Handlers for Image
  const handleImageTextChange = (e) => {
    const { name, value } = e.target;
    setFormImage((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageCheckboxChange = (e) => {
    const { name, checked } = e.target;
    setFormImage((prev) => ({
      ...prev,
      [name]: checked ? 'Yes' : 'No',
    }));
  };

  const handleImageSetDateChange = (e) => {
    const checked = e.target.checked;
    setFormImage((prev) => ({
      ...prev,
      popup_trigger_date: checked ? 'Set Date' : '',
      ...(checked ? {} : { startDate: '', endDate: '' }),
    }));
  };

  const handleImageRadioChange = (e) => {
    const { name, value } = e.target;
    setFormImage((prev) => ({ ...prev, [name]: value }));
  };

  const handleImageFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFormImage((prev) => ({
      ...prev,
      image: file,
      imagePreview: URL.createObjectURL(file),
    }));
  };

  const handleRemoveImage = async () => {
    if (!formImage.popup_id) {
      setFormImage((prev) => ({ ...prev, image: null, imagePreview: '' }));
      return;
    }
    try {
      const fd = new FormData();
      fd.append('issetDeleteImage', '1');
      fd.append('popup_id', formImage.popup_id);
      const res = await fetch(POPUP_DELETE_IMAGE, { method: 'POST', body: fd, credentials: 'include' });
      const json = await res.json();
      if (json.status === true) {
        setFormImage((prev) => ({ ...prev, image: null, imagePreview: '' }));
        setSuccessImage('Image removed successfully');
      } else {
        setErrorImage(getFirstErrorMessage(json.errorMsg) || 'Failed to remove image');
      }
    } catch (err) {
      setErrorImage('Network error while removing image');
    }
  };

  const handleImageSubmit = async (e) => {
    e.preventDefault();
    setSavingImage(true);
    setErrorImage(null);
    setSuccessImage(null);
    const fd = new FormData();
    fd.append('issetAddEditPopup', '1');
    if (formImage.popup_id) fd.append('popup_id', formImage.popup_id);
    fd.append('title', formImage.title);
    fd.append('description', formImage.description);
    fd.append('button', formImage.button);
    fd.append('button_url', formImage.button_url);
    fd.append('button_target', formImage.button_target);
    fd.append('image_showOnMobile', formImage.image_showOnMobile);
    fd.append('popup_trigger', formImage.popup_trigger);
    fd.append('popup_trigger_url', formImage.popup_trigger_url || '');
    fd.append('popup_trigger_date', formImage.popup_trigger_date || '');
    fd.append('startDate', formImage.startDate || '');
    fd.append('endDate', formImage.endDate || '');
    fd.append('status', formImage.status);
    if (formImage.image) fd.append('image', formImage.image);
    try {
      const res = await fetch(POPUP_SAVE, { method: 'POST', body: fd, credentials: 'include' });
      const json = await res.json();
      if (json.status === true) {
        setSuccessImage('Image popup saved successfully!');
        fetchExistingImagePopup();
      } else {
        setErrorImage(getFirstErrorMessage(json.errorMsg) || 'Failed to save image popup');
      }
    } catch (err) {
      setErrorImage('Network error. Please check connection.');
    } finally {
      setSavingImage(false);
    }
  };

  // Handlers for PDF
  const handlePdfTextChange = (e) => {
    const { name, value } = e.target;
    setFormPdf((prev) => ({ ...prev, [name]: value }));
  };

  const handlePdfSetDateChange = (e) => {
    const checked = e.target.checked;
    setFormPdf((prev) => ({
      ...prev,
      popup_trigger_date: checked ? 'Set Date' : '',
      ...(checked ? {} : { startDate: '', endDate: '' }),
    }));
  };

  const handlePdfRadioChange = (e) => {
    const { name, value } = e.target;
    setFormPdf((prev) => ({ ...prev, [name]: value }));
  };

  const handlePdfFileChange = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setFormPdf((prev) => ({
      ...prev,
      pdf: file,
      pdfPreview: URL.createObjectURL(file),
      pdfName: file.name,
    }));
  };

  const handleRemovePdf = async () => {
    try {
      const fd = new FormData();
      fd.append('issetDeletePdf', '1');
      const res = await fetch(POPUP_DELETE_PDF, { method: 'POST', body: fd, credentials: 'include' });
      const json = await res.json();
      if (json.status === true) {
        setFormPdf((prev) => ({ ...prev, pdf: null, pdfPreview: '', pdfName: '' }));
        setSuccessPdf('PDF removed successfully');
      } else {
        setErrorPdf(getFirstErrorMessage(json.errorMsg) || 'Failed to remove PDF');
      }
    } catch (err) {
      setErrorPdf('Network error while removing PDF');
    }
  };

  const handlePdfSubmit = async (e) => {
    e.preventDefault();
    setSavingPdf(true);
    setErrorPdf(null);
    setSuccessPdf(null);
    const fd = new FormData();
    fd.append('issetAddEditPopupPdf', '1');
    fd.append('popup_trigger', formPdf.popup_trigger);
    fd.append('popup_trigger_url', formPdf.popup_trigger_url || '');
    fd.append('popup_trigger_date', formPdf.popup_trigger_date || '');
    fd.append('startDate', formPdf.startDate || '');
    fd.append('endDate', formPdf.endDate || '');
    fd.append('status', formPdf.status);
    if (formPdf.pdf) fd.append('pdf', formPdf.pdf);
    try {
      const res = await fetch(POPUP_PDF_SAVE, { method: 'POST', body: fd, credentials: 'include' });
      const json = await res.json();
      if (json.status === true) {
        setSuccessPdf('PDF popup saved successfully!');
        fetchExistingPdfPopup();
      } else {
        setErrorPdf(getFirstErrorMessage(json.errorMsg) || 'Failed to save PDF popup');
      }
    } catch (err) {
      setErrorPdf('Network error. Please check connection.');
    } finally {
      setSavingPdf(false);
    }
  };

  if (loadingImage || loadingPdf) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '60vh' }}>
        <CircularProgress />
      </Box>
    );
  }

  const isImageDateEnabled = formImage.popup_trigger_date === 'Set Date';
  const hasImage = !!formImage.imagePreview;

  const isPdfDateEnabled = formPdf.popup_trigger_date === 'Set Date';
  const hasPdf = !!formPdf.pdfPreview;

  return (
    <Box sx={{ p: 0 }}>
      <Typography variant="h4" fontWeight={800} mb={3}>
        Manage Popups
      </Typography>

      {/* Image Popup Section */}
      <Box component="form" onSubmit={handleImageSubmit}>

        {errorImage && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setErrorImage(null)}>{errorImage}</Alert>}
        {successImage && <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccessImage(null)}>{successImage}</Alert>}

        <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', lg: 'row' }, mb: 6 }}>
          {/* Left - Image Controls */}
          <Box sx={{ width: { lg: 300 }, flexShrink: 0 }}>
            <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography fontWeight={600}>Title</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <TextField fullWidth name="title" value={formImage.title} onChange={handleImageTextChange} size="small" />
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography fontWeight={600}>Description</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <TextField fullWidth multiline rows={4} name="description" value={formImage.description} onChange={handleImageTextChange} />
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography fontWeight={600}>Button</Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <TextField label="Button Text" name="button" value={formImage.button} onChange={handleImageTextChange} size="small" />
                <TextField label="Button URL" name="button_url" value={formImage.button_url} onChange={handleImageTextChange} size="small" />
                <FormControlLabel
                  control={<Checkbox checked={formImage.button_target === '_blank'} onChange={(e) => setFormImage(p => ({ ...p, button_target: e.target.checked ? '_blank' : '_self' }))} />}
                  label="Open in new tab"
                />
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography fontWeight={600}>Image</Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {hasImage ? (
                  <Box sx={{ position: 'relative', display: 'inline-block', width: '100%' }}>
                    <Box
                      component="img"
                      src={formImage.imagePreview}
                      alt="Image preview"
                      sx={{ width: '100%', height: 180, objectFit: 'cover', borderRadius: 2, boxShadow: 1 }}
                    />
                    <IconButton
                      size="small"
                      onClick={handleRemoveImage}
                      sx={{ position: 'absolute', top: 8, right: 8, bgcolor: 'error.main', color: 'white', '&:hover': { bgcolor: 'error.dark' } }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      width: '100%',
                      height: 180,
                      border: '2px dashed',
                      borderColor: 'divider',
                      borderRadius: 2,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: 'action.hover',
                      color: 'text.secondary',
                      gap: 1,
                    }}
                  >
                    <ImageIcon sx={{ fontSize: 48, opacity: 0.6 }} />
                    <Typography variant="body2" align="center">
                      No image selected<br />
                      Click "Upload Image" to add popup
                    </Typography>
                  </Box>
                )}

                <Button component="label" variant="outlined" startIcon={<CloudUploadIcon />} size="small">
                  Upload Image
                  <input type="file" hidden accept="image/jpeg,image/png" onChange={handleImageFileChange} />
                </Button>

                <FormControlLabel
                  control={<Checkbox checked={formImage.image_showOnMobile === 'Yes'} onChange={handleImageCheckboxChange} name="image_showOnMobile" />}
                  label="Show on mobile"
                />
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography fontWeight={600}>Trigger</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <FormControl component="fieldset">
                  <RadioGroup name="popup_trigger" value={formImage.popup_trigger} onChange={handleImageRadioChange}>
                    <FormControlLabel value="Only on the homepage" control={<Radio />} label="Only on homepage" />
                    <FormControlLabel value="Show on all pages" control={<Radio />} label="Show on all pages" />
                    <FormControlLabel value="Custom page" control={<Radio />} label="Custom page / URL" />
                  </RadioGroup>
                  {formImage.popup_trigger === 'Custom page' && (
                    <TextField
                      label="Trigger URL"
                      name="popup_trigger_url"
                      value={formImage.popup_trigger_url}
                      onChange={handleImageTextChange}
                      fullWidth
                      size="small"
                      sx={{ mt: 2 }}
                    />
                  )}
                </FormControl>
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography fontWeight={600}>Date Range</Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <FormControlLabel
                  control={<Checkbox checked={isImageDateEnabled} onChange={handleImageSetDateChange} />}
                  label="Enable date range (Set Date)"
                />
                <TextField
                  label="Start Date"
                  type="date"
                  name="startDate"
                  value={formImage.startDate}
                  onChange={handleImageTextChange}
                  InputLabelProps={{ shrink: true }}
                  disabled={!isImageDateEnabled}
                  fullWidth
                  size="small"
                />
                <TextField
                  label="End Date"
                  type="date"
                  name="endDate"
                  value={formImage.endDate}
                  onChange={handleImageTextChange}
                  InputLabelProps={{ shrink: true }}
                  disabled={!isImageDateEnabled}
                  fullWidth
                  size="small"
                />
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography fontWeight={600}>Status</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <FormControl component="fieldset">
                  <RadioGroup row name="status" value={formImage.status} onChange={handleImageRadioChange}>
                    <FormControlLabel value="Active" control={<Radio />} label="Active" />
                    <FormControlLabel value="Deactive" control={<Radio />} label="Deactive" />
                  </RadioGroup>
                </FormControl>
              </AccordionDetails>
            </Accordion>

            <Button
              type="submit"
              variant="contained"
              fullWidth
              size="medium"
              startIcon={savingImage ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
              disabled={savingImage}
              sx={{ mt: 4, py: 1.2 }}
            >
              {savingImage ? 'Saving...' : 'Save Image Popup'}
            </Button>
          </Box>

          {/* Right - Image Preview */}
          <Box sx={{ flex: 1 }}>
            <Paper
              elevation={3}
              sx={{
                p: 3,
                borderRadius: 3,
                background: '#ffffff',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                textAlign: 'center',
              }}
            >
              {hasImage ? (
                <Box sx={{ mb: 2, width: '100%' }}>
                  <img
                    src={formImage.imagePreview}
                    alt="Popup preview"
                    style={{
                      width: '100%',
                      height: 'auto',
                      maxHeight: '280px',
                      objectFit: 'cover',
                      boxShadow: '0 6px 20px rgba(0,0,0,0.12)',
                    }}
                  />
                </Box>
              ) : (
                <Box
                  sx={{
                    width: '100%',
                    height: 220,
                    mb: 2,
                    borderRadius: 2,
                    backgroundColor: '#f8f9fc',
                    border: '2px dashed #d1d9e6',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#9ca3af',
                  }}
                >
                  <ImageIcon sx={{ fontSize: 64, mb: 2, opacity: 0.5 }} />
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    No image selected yet
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1, opacity: 0.8 }}>
                    Upload image to see preview here
                  </Typography>
                </Box>
              )}

              <Box sx={{ maxWidth: 480, width: '100%' }}>
                {formImage.title && (
                  <Typography variant="h5" fontWeight={700} gutterBottom sx={{ mb: 2 }}>
                    {formImage.title}
                  </Typography>
                )}

                {formImage.description && (
                  <Typography variant="body1" sx={{ mb: 2}}>
                    {formImage.description}
                  </Typography>
                )}

                {formImage.button && (
                  <Button
                    variant="contained"
                    color="primary"
                    size="large"
                    href={formImage.button_url || '#'}
                    target={formImage.button_target}
                    sx={{ minWidth: 180, px: 3, py: 1, fontSize: '12px', boxShadow: '0 4px 12px rgba(0,0,0,0.1)' }}
                  >
                    {formImage.button}
                  </Button>
                )}

              </Box>
            </Paper>
          </Box>
        </Box>
      </Box>

      <Divider sx={{ my: 6 }} />

      {/* PDF Popup Section */}
      <Box component="form" onSubmit={handlePdfSubmit}>
        <Typography variant="h5" fontWeight={700} mb={2}>
          PDF Popup
        </Typography>

        {errorPdf && <Alert severity="error" sx={{ mb: 3 }} onClose={() => setErrorPdf(null)}>{errorPdf}</Alert>}
        {successPdf && <Alert severity="success" sx={{ mb: 3 }} onClose={() => setSuccessPdf(null)}>{successPdf}</Alert>}

        <Box sx={{ display: 'flex', gap: 3, flexDirection: { xs: 'column', lg: 'row' } }}>
          {/* Left - PDF Controls */}
          <Box sx={{ width: { lg: 300 }, flexShrink: 0 }}>
            <Accordion defaultExpanded>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography fontWeight={600}>PDF</Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                {hasPdf ? (
                  <Box sx={{ position: 'relative', display: 'flex', alignItems: 'center', width: '100%', bgcolor: 'action.hover', p: 1, borderRadius: 1 }}>
                    <PictureAsPdfIcon sx={{ mr: 2, color: 'error.main' }} />
                    <Typography variant="body2" noWrap sx={{ flex: 1 }}>
                      {formPdf.pdfName}
                    </Typography>
                    <IconButton
                      size="small"
                      onClick={handleRemovePdf}
                      sx={{ color: 'error.main' }}
                    >
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ) : (
                  <Box
                    sx={{
                      width: '100%',
                      height: 100,
                      border: '2px dashed',
                      borderColor: 'divider',
                      borderRadius: 2,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      bgcolor: 'action.hover',
                      color: 'text.secondary',
                      gap: 1,
                    }}
                  >
                    <PictureAsPdfIcon sx={{ fontSize: 32, opacity: 0.6 }} />
                    <Typography variant="body2" align="center">
                      No PDF selected
                    </Typography>
                  </Box>
                )}

                <Button component="label" variant="outlined" startIcon={<CloudUploadIcon />} size="small">
                  Upload PDF
                  <input type="file" hidden accept="application/pdf" onChange={handlePdfFileChange} />
                </Button>
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography fontWeight={600}>Trigger</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <FormControl component="fieldset">
                  <RadioGroup name="popup_trigger" value={formPdf.popup_trigger} onChange={handlePdfRadioChange}>
                    <FormControlLabel value="Only on the homepage" control={<Radio />} label="Only on homepage" />
                    <FormControlLabel value="Show on all pages" control={<Radio />} label="Show on all pages" />
                    <FormControlLabel value="Custom page" control={<Radio />} label="Custom page / URL" />
                  </RadioGroup>
                  {formPdf.popup_trigger === 'Custom page' && (
                    <TextField
                      label="Trigger URL"
                      name="popup_trigger_url"
                      value={formPdf.popup_trigger_url}
                      onChange={handlePdfTextChange}
                      fullWidth
                      size="small"
                      sx={{ mt: 2 }}
                    />
                  )}
                </FormControl>
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography fontWeight={600}>Date Range</Typography>
              </AccordionSummary>
              <AccordionDetails sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                <FormControlLabel
                  control={<Checkbox checked={isPdfDateEnabled} onChange={handlePdfSetDateChange} />}
                  label="Enable date range (Set Date)"
                />
                <TextField
                  label="Start Date"
                  type="date"
                  name="startDate"
                  value={formPdf.startDate}
                  onChange={handlePdfTextChange}
                  InputLabelProps={{ shrink: true }}
                  disabled={!isPdfDateEnabled}
                  fullWidth
                  size="small"
                />
                <TextField
                  label="End Date"
                  type="date"
                  name="endDate"
                  value={formPdf.endDate}
                  onChange={handlePdfTextChange}
                  InputLabelProps={{ shrink: true }}
                  disabled={!isPdfDateEnabled}
                  fullWidth
                  size="small"
                />
              </AccordionDetails>
            </Accordion>

            <Accordion>
              <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                <Typography fontWeight={600}>Status</Typography>
              </AccordionSummary>
              <AccordionDetails>
                <FormControl component="fieldset">
                  <RadioGroup row name="status" value={formPdf.status} onChange={handlePdfRadioChange}>
                    <FormControlLabel value="Active" control={<Radio />} label="Active" />
                    <FormControlLabel value="Deactive" control={<Radio />} label="Deactive" />
                  </RadioGroup>
                </FormControl>
              </AccordionDetails>
            </Accordion>

            <Button
              type="submit"
              variant="contained"
              fullWidth
              size="medium"
              startIcon={savingPdf ? <CircularProgress size={20} color="inherit" /> : <SaveIcon />}
              disabled={savingPdf}
              sx={{ mt: 4, py: 1.2 }}
            >
              {savingPdf ? 'Saving...' : 'Save PDF Popup'}
            </Button>
          </Box>

          {/* Right - PDF Preview */}
          <Box sx={{ flex: 1 }}>
            
              {hasPdf ? (
                <Box sx={{ width: '100%', height: '600px', borderRadius: 2, overflow: 'hidden', boxShadow: 3 }}>
                  <embed
                    src={`${formPdf.pdfPreview}#toolbar=0&navpanes=0&scrollbar=0`}
                    type="application/pdf"
                    width="100%"
                    height="100%"
                    title="PDF Preview"
                  />
                </Box>
              ) : (
                <Box
                  sx={{
                    width: '100%',
                    height: 500,
                    borderRadius: 2,
                    backgroundColor: '#f8f9fc',
                    border: '2px dashed #d1d9e6',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: '#9ca3af',
                  }}
                >
                  <PictureAsPdfIcon sx={{ fontSize: 80, mb: 2, opacity: 0.5 }} />
                  <Typography variant="body1" sx={{ fontWeight: 500 }}>
                    No PDF selected yet
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1, opacity: 0.8 }}>
                    Upload PDF to see preview here
                  </Typography>
                </Box>
              )}
            
          </Box>
        </Box>
      </Box>
    </Box>
  );
}